import {useCallback, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {tripDelayService} from '@/services/tripDelayService'

/**
 * REPORTING A DELAY, and putting the answer where the rest of the screen will find it.
 *
 * ── ⚠️ THE TRIP CACHE IS PATCHED BY HAND, NOT INVALIDATED ─────────────────────────
 * Everything about a delay — the countdown banner, whether the entry card is offered, the
 * five history bubbles — is derived from the TRIP, not from this call's return value. An
 * invalidate would leave the screen showing the old truth for as long as the refetch took,
 * and the passenger would see the "Experiencing a delay?" card still sitting there after
 * they had just used it. Writing the record straight into the cache makes the whole screen
 * correct in the same frame the request resolves.
 *
 * The 2s trip poll and the socket's `trip.delay.requested` reconcile it moments later; this
 * patch only has to be right for those two seconds.
 *
 * ── ⚠️ AND THE PICKUP TIME MOVES WITH IT ──────────────────────────────────────────
 * A granted delay shifts `scheduled_at`. It is written here too, because the same trip
 * object is what every other panel reads the pickup time from — leaving it stale would
 * show a passenger a pickup time that the delay they just requested has already changed.
 */

/** The error the server returns when a window is already running. Its own copy. */
const ACTIVE_KEY = 'requested_delay_active'

export function useReportTripDelay(tripId) {
    const queryClient = useQueryClient()
    const [submittingMinutes, setSubmittingMinutes] = useState(null)
    const [error, setError] = useState('')

    const report = useCallback(async ({reasonCode, reasonText, minutes}) => {
        setSubmittingMinutes(minutes)
        setError('')

        const result = await tripDelayService.report(tripId, {reasonCode, reasonText, minutes})
        setSubmittingMinutes(null)

        if (!result.success) {
            setError(result.key === ACTIVE_KEY
                ? 'A delay is already active for this trip.'
                : (result.error || 'Could not report this delay.'))
            return false
        }

        queryClient.setQueryData(['trip', String(tripId)], patch(result))
        /* ⚠️ BOTH KEY SPELLINGS. `useTripDetails` is called with a route param (a string)
           in some places and a numeric `trip.id` in others, and React Query keys are
           compared structurally — so `['trip','5821']` and `['trip',5821]` are two caches.
           Patching one and not the other leaves whichever panel used the other spelling
           showing the pre-delay trip. */
        queryClient.setQueryData(['trip', Number(tripId)], patch(result))
        return true
    }, [queryClient, tripId])

    return {report, submittingMinutes, error, clearError: () => setError('')}
}

/**
 * The cache updater.
 *
 * ⚠️ IT DOES NOTHING IF THE TRIP IS NOT ALREADY CACHED. Returning a half-built trip from
 * here would install an object with a `requested_delays` array and nothing else as the
 * trip detail, and every panel reading it would render its empty state.
 *
 * ⚠️ NEWEST FIRST, matching the payload's own order — `delayFeedItems` reverses it, so a
 * record prepended in the wrong order would render out of sequence until the next poll.
 */
function patch(result) {
    return (old) => {
        if (!old?.trip) return old
        return {
            ...old,
            trip: {
                ...old.trip,
                scheduled_at: result.scheduledAt || old.trip.scheduled_at,
                active_requested_delay: result.delay,
                requested_delays: [result.delay, ...(old.trip.requested_delays || [])],
            },
        }
    }
}

export default useReportTripDelay
